import { Row } from '@10xLabs/react-web/components/Row'
import { safeFormatMessage } from '@10xlabs/utils'
import { Formik } from 'formik'
import { ReactElement } from 'react'
import { useIntl, FormattedMessage, defineMessages } from 'react-intl'
import { NavLink } from 'react-router-dom'
import styled from 'styled-components'

import { Button } from 'components/v2/Button'
import Card from '@10xLabs/react-web/components/Card'
import { Input } from 'components/Input'
import { Textarea } from 'components/v2/Textarea'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { Column } from '@10xLabs/react-web/components/Column'
import Heading from 'components/v2/Heading'
import { Label } from 'components/v2/Label'
import { Radio } from 'components/v2/Radio'
import { SelectState } from 'features/Cities/components/SelectState'
import { SelectCompanyRole } from 'features/CompanyRoles/components/SelectCompanyRole'
import { INewCompanyFormProps } from 'features/Companies/components/NewCompany/types'
import { verticalMessages } from 'features/Companies/components/Vertical/messages'
import { CompanyV2VerticalInput } from 'gql/generated/graphql'
import { getUnprocessableErrors } from 'utils/error'
import { newCompanySchema } from './schema'

const VERTICALS_LIST = [
  CompanyV2VerticalInput.Travel,
  CompanyV2VerticalInput.City,
  CompanyV2VerticalInput.Team,
  CompanyV2VerticalInput.Rental,
  CompanyV2VerticalInput.Mobile
]

const messages = defineMessages({
  namePlaceholder: {
    id: 'companies.new.form.name.placeholder',
    defaultMessage: 'Enter name'
  },
  phonePlaceholder: {
    id: 'companies.new.form.phone.placeholder',
    defaultMessage: 'Telephone (10-20 Digits)'
  },
  line1Placeholder: {
    id: 'companies.new.form.address.line1.placeholder',
    defaultMessage: 'E.g. 123 Main St.'
  },
  line2Placeholder: {
    id: 'companies.new.form.address.line2.placeholder',
    defaultMessage: 'Enter additional information'
  },
  cityPlaceholder: {
    id: 'companies.new.form.address.city.placeholder',
    defaultMessage: 'Enter City'
  },
  zipCodePlaceholder: {
    id: 'companies.new.form.address.zipCode.placeholder',
    defaultMessage: 'Enter Zip code'
  },
  commentsPlaceholder: {
    id: 'companies.new.form.comments.placeholder',
    defaultMessage: 'Enter indications'
  }
})

const RadioWrapper = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: space-between;

  input {
    margin-left: 0;
  }
`

const NewCompanyForm = ({
  values,
  touched,
  errors,
  handleChange,
  setFieldValue,
  handleBlur,
  handleSubmit,
  error,
  loading,
  isEdit
}: INewCompanyFormProps): ReactElement => {
  const intl = useIntl()

  const handleVerticalChange = ({ target: { id: value } }: React.ChangeEvent<HTMLInputElement>): void => {
    setFieldValue('vertical', value)
  }

  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      <Card.Content noHeader>
        <Row>
          <Column width="50%">
            <Input
              id="name"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.name}
              placeholder={intl.formatMessage(messages.namePlaceholder)}
              touched={touched.name}
              errors={errors.name}
              label={<FormattedMessage id="companies.new.form.name.label" defaultMessage="Name" />}
            />
          </Column>
          <Column width="50%">
            <Label>
              <FormattedMessage id="companies.new.form.vertical.label" defaultMessage="Vertical" />
            </Label>
            <RadioWrapper>
              {VERTICALS_LIST.map((vertical): ReactElement => {
                return (
                  <Radio
                    id={vertical}
                    key={vertical}
                    name="vertical"
                    checked={values.vertical === vertical}
                    onChange={handleVerticalChange}
                    disabled={isEdit}
                  >
                    {safeFormatMessage({
                      intl,
                      key: vertical,
                      messages: verticalMessages
                    })}
                  </Radio>
                )
              })}
            </RadioWrapper>
            {touched.vertical && errors.vertical && <ErrorMessage>{errors.vertical}</ErrorMessage>}
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Input
              id="phone"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.phone}
              placeholder={intl.formatMessage(messages.phonePlaceholder)}
              touched={touched.phone}
              errors={errors.phone}
              label={<FormattedMessage id="companies.new.form.phone.label" defaultMessage="Telephone" />}
            />
          </Column>
          <Column width="50%">
            <SelectCompanyRole
              id="policyId"
              companyRoleId={values.policyId}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </Column>
        </Row>
        <Row>
          <Column>
            <Heading.H5>
              <FormattedMessage id="dictionary.address" defaultMessage="Address" />
            </Heading.H5>
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Input
              id="address.line1"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.address.line1}
              placeholder={intl.formatMessage(messages.line1Placeholder)}
              touched={touched.address && touched.address.line1}
              errors={errors.address && errors.address.line1}
              label={<FormattedMessage id="companies.new.form.address.line1.label" defaultMessage="Street" />}
            />
          </Column>
          <Column width="50%">
            <Input
              id="address.line2"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.address.line2 ?? ''}
              placeholder={intl.formatMessage(messages.line2Placeholder)}
              touched={touched.address && touched.address.line2}
              errors={errors.address && errors.address.line2}
              label={
                <FormattedMessage
                  id="companies.new.form.address.line2.label"
                  defaultMessage="Additional information (Optional)"
                />
              }
            />
          </Column>
        </Row>
        <Row>
          <Column width="25%">
            <Input
              id="address.cityName"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.address.cityName}
              placeholder={intl.formatMessage(messages.cityPlaceholder)}
              touched={touched.address?.cityName}
              errors={errors.address?.cityName}
              label={<FormattedMessage id="companies.new.form.address.city.label" defaultMessage="City" />}
            />
          </Column>
          <Column width="25%">
            <Input
              id="address.zipCode"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.address.zipCode}
              placeholder={intl.formatMessage(messages.zipCodePlaceholder)}
              touched={touched.address && touched.address.zipCode}
              errors={errors.address && errors.address.zipCode}
              label={<FormattedMessage id="companies.new.form.address.zipCode.label" defaultMessage="ZIP code" />}
            />
          </Column>
          <Column width="50%">
            <SelectState
              id="address.stateCode"
              value={values.address.stateCode}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.address && touched.address.stateCode}
              errors={errors.address && errors.address.stateCode}
              label={<FormattedMessage id="dictionary.state" defaultMessage="State" />}
            />
          </Column>
        </Row>
        <Row>
          <Column>
            <Textarea
              id="comments"
              value={values.comments}
              onChange={handleChange}
              label={
                <FormattedMessage id="companies.new.form.comments.label" defaultMessage="Indications (Optional)" />
              }
              placeholder={intl.formatMessage(messages.commentsPlaceholder)}
            />
          </Column>
        </Row>
      </Card.Content>
      <Card.Footer>
        {error && (
          <div>
            <ErrorMessage errors={getUnprocessableErrors(error)}>
              <FormattedMessage
                id="companies.new.form.error"
                defaultMessage="There was an error while creating your company"
              />
            </ErrorMessage>
          </div>
        )}
        <Card.FooterActions>
          <Button as={NavLink} to="/companies" kind="cancel">
            <FormattedMessage id="companies.new.form.cancel" defaultMessage="Cancel" />
          </Button>
          <Button type="submit" kind="primary" loading={loading}>
            {isEdit ? (
              <FormattedMessage id="dictionary.update" defaultMessage="Update" />
            ) : (
              <FormattedMessage id="dictionary.create" defaultMessage="Create" />
            )}
          </Button>
        </Card.FooterActions>
      </Card.Footer>
    </form>
  )
}

export const NewCompany = ({ company = {}, onSubmit, error, loading, isEdit }: TSFixMe): ReactElement => {
  const intl = useIntl()

  const { address = {} } = company

  const initialAddress = {
    line1: address.line1 || '',
    line2: address.line2 || '',
    cityName: address.cityName || '',
    zipCode: address.zipCode || '',
    stateCode: address.stateCode || '',
    countryCode: 'MX'
  }

  const initialCompany = {
    name: company.name ?? '',
    phone: company.phone ?? '',
    address: initialAddress,
    comments: company.comments ?? '',
    policyId: company.policy?.id ?? '',
    vertical: company.vertical ?? ''
  }

  return (
    <Formik initialValues={initialCompany} validationSchema={newCompanySchema(intl)} onSubmit={onSubmit}>
      {(props): ReactElement => {
        // @ts-expect-error FIXME
        return <NewCompanyForm {...props} error={error} loading={loading} isEdit={isEdit} />
      }}
    </Formik>
  )
}
